/**
 * Guide Approval Queue Page
 *
 * Admin interface for managing guide approval workflow with queue management,
 * requirements tracking, and approval actions.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-146 - Guide Approval Workflow Enhancement (34 SP)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users as _Users,
  Filter as _Filter,
  Search,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronLeft as _ChevronLeft,
  ChevronRight as _ChevronRight
} from 'lucide-react';

import apiRequest from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ApprovalQueueTable from '../../components/admin/ApprovalQueueTable';
import RequirementsList from '../../components/admin/RequirementsList';
import Pagination from '../../components/admin/Pagination';
import SearchBar from '../../components/admin/SearchBar';
import FilterDropdown from '../../components/admin/FilterDropdown';
import ConfirmModal from '../../components/shared/ConfirmModal';
import PromptModal from '../../components/shared/PromptModal';
import { useGuideApprovals, useGuideApproval, useGuideRequirements } from '../../hooks/admin/useGuideApprovals';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'pending_information', label: 'Pending Information' },
  { value: 'pending_verification', label: 'Pending Verification' },
  { value: 'pending_licensing', label: 'Pending Licensing' },
  { value: 'pending_insurance', label: 'Pending Insurance' },
  { value: 'pending_training', label: 'Pending Training' },
  { value: 'conditionally_approved', label: 'Conditionally Approved' },
];

// SCRUM-1861: Quick filter tabs for the most common statuses
const QUICK_FILTER_TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'pending_verification', label: 'Pending Verification' },
];

function DocumentLinks({ documents }) {
  if (!documents || documents.length === 0) {
    return <span style={{ fontSize: '12px', color: '#9ca3af' }}>No documents uploaded</span>;
  }

  const idDocs = documents.filter(d => d.assetPurpose === 'id_document');
  const certDocs = documents.filter(d => d.assetPurpose === 'certification');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {idDocs.map(doc => (
        <a
          key={doc.id}
          href={doc.cdnUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'underline' }}
        >
          Government ID{doc.originalFilename ? ` (${doc.originalFilename})` : ''}
        </a>
      ))}
      {certDocs.map(doc => (
        <a
          key={doc.id}
          href={doc.cdnUrl || '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: '12px', color: '#059669', textDecoration: 'underline' }}
        >
          Certification{doc.originalFilename ? ` (${doc.originalFilename})` : ''}
        </a>
      ))}
    </div>
  );
}

const SORT_OPTIONS = [
  { value: 'profile_created_at', label: 'Submission Date' },
  { value: 'guide_name', label: 'Guide Name' },
  { value: 'approval_status', label: 'Status' },
];

export default function GuideApprovalQueue() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // Approval queue state — filters are owned by the hook; initial values set here
  const [initialFilters] = useState({
    status: '',
    search: '',
    has_urgent: false,
    sort_by: 'profile_created_at',
    sort_order: 'desc',
    limit: 50,
    offset: 0,
  });

  const { queue, loading, error, filters, updateFilters, refresh } = useGuideApprovals(initialFilters);

  // Selected guide state
  const [selectedGuideId, setSelectedGuideId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [approveConfirm, setApproveConfirm] = useState({ isOpen: false, guideId: null });
  const [rejectPrompt, setRejectPrompt] = useState({ isOpen: false, guideId: null });
  const [requestInfoPrompt, setRequestInfoPrompt] = useState({ isOpen: false, guideId: null });
  const [suspendPrompt, setSuspendPrompt] = useState({ isOpen: false, guideId: null });
  const [toast, setToast] = useState(null);

  // Handle filter changes
  const handleStatusFilter = (status) => {
    updateFilters({ status, offset: 0 });
  };

  const handleSearch = (search) => {
    updateFilters({ search, offset: 0 });
  };

  const handleSort = (field) => {
    const newOrder = filters.sort_by === field && filters.sort_order === 'desc' ? 'asc' : 'desc';
    updateFilters({ sort_by: field, sort_order: newOrder });
  };

  const handlePageChange = (newOffset) => {
    updateFilters({ offset: newOffset });
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Handle guide actions
  const handleViewGuide = (guideId) => {
    setSelectedGuideId(guideId);
    setShowDetailsModal(true);
  };

  const handleApprove = (guideId) => {
    setApproveConfirm({ isOpen: true, guideId });
  };

  const executeApprove = async (guideId) => {
    setApproveConfirm({ isOpen: false, guideId: null });
    try {
      await apiRequest(`/api/v1/admin/guide-approvals/${guideId}/approve`, { method: 'PUT' });
      showToast('Guide approved successfully');
      refresh();
    } catch (err) {
      showToast(err.message || 'Failed to approve guide', 'error');
    }
  };

  const handleReject = (guideId) => {
    setRejectPrompt({ isOpen: true, guideId });
  };

  const executeReject = async (reason) => {
    const guideId = rejectPrompt.guideId;
    setRejectPrompt({ isOpen: false, guideId: null });
    try {
      await apiRequest(`/api/v1/admin/guide-approvals/${guideId}/reject`, {
        method: 'PUT',
        body: JSON.stringify({ reason }),
      });
      showToast('Guide rejected');
      refresh();
    } catch (err) {
      showToast(err.message || 'Failed to reject guide', 'error');
    }
  };

  const handleRequestInfo = (guideId) => {
    setRequestInfoPrompt({ isOpen: true, guideId });
  };

  const executeRequestInfo = async (notes) => {
    const guideId = requestInfoPrompt.guideId;
    setRequestInfoPrompt({ isOpen: false, guideId: null });
    try {
      await apiRequest(`/api/v1/admin/guide-approvals/${guideId}/request-information`, {
        method: 'PUT',
        body: JSON.stringify({ notes }),
      });
      showToast('Information requested from guide');
      refresh();
    } catch (err) {
      showToast(err.message || 'Failed to request information', 'error');
    }
  };

  const handleSuspend = (guideId) => {
    setSuspendPrompt({ isOpen: true, guideId });
  };

  const executeSuspend = async (reason) => {
    const guideId = suspendPrompt.guideId;
    setSuspendPrompt({ isOpen: false, guideId: null });
    try {
      await apiRequest(`/api/v1/admin/guide-approvals/${guideId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'suspended', change_reason: reason }),
      });
      showToast('Guide suspended');
      refresh();
    } catch (err) {
      showToast(err.message || 'Failed to suspend guide', 'error');
    }
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="approvals"
      onNavigate={handleNavigate}
      user={{ name: authUser?.fullName || authUser?.firstName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
    >
      {/* Page Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            Guide Approval Queue
          </h1>
          <button
            onClick={refresh}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--space-2) var(--space-4)',
              border: 'none',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-white)',
              backgroundColor: loading ? 'var(--color-gray-300)' : 'var(--color-primary)',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all var(--transition-fast)',
              gap: 'var(--space-2)'
            }}
          >
            <RefreshCw style={{
              width: '16px',
              height: '16px',
              animation: loading ? 'spin 1s linear infinite' : 'none'
            }} />
            Refresh
          </button>
        </div>
      </div>

      <div className="space-y-6">

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3">
                <_Users className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">In Queue</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {queue.pagination.total}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Urgent</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {queue.items.filter(g => g.requirements.urgent > 0).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                <Search className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending Info</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {queue.items.filter(g => g.status === 'pending_information').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Ready to Approve</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {queue.items.filter(g => g.requirements.pending === 0 && g.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Filter Tabs (SCRUM-1861) */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
          {QUICK_FILTER_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => handleStatusFilter(tab.value)}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: 500,
                borderRadius: '6px',
                border: filters.status === tab.value ? '2px solid #059669' : '1px solid #d1d5db',
                background: filters.status === tab.value ? '#ecfdf5' : '#ffffff',
                color: filters.status === tab.value ? '#065f46' : '#374151',
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[300px]">
              <SearchBar
                value={filters.search}
                onChange={handleSearch}
                placeholder="Search by name or email..."
              />
            </div>

            <FilterDropdown
              label="Status"
              value={filters.status}
              onChange={handleStatusFilter}
              options={STATUS_OPTIONS}
            />

            <FilterDropdown
              label="Sort By"
              value={filters.sort_by}
              onChange={(value) => updateFilters({ sort_by: value })}
              options={SORT_OPTIONS}
            />

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.has_urgent}
                onChange={(e) => updateFilters({ has_urgent: e.target.checked, offset: 0 })}
                className="rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
              />
              <span className="text-sm font-medium text-gray-700">Urgent Only</span>
            </label>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <XCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Loading Queue</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Approval Queue Table */}
        <div className="bg-white rounded-lg shadow">
          <ApprovalQueueTable
            guides={queue.items}
            sortField={filters.sort_by}
            sortOrder={filters.sort_order}
            onSort={handleSort}
            onViewGuide={handleViewGuide}
            onApprove={handleApprove}
            onReject={handleReject}
            onRequestInfo={handleRequestInfo}
            onSuspend={handleSuspend}
            loading={loading}
          />

          {/* SCRUM-1861: Document links for pending_verification guides */}
          {filters.status === 'pending_verification' && !loading && queue.items.length > 0 && (
            <div style={{ borderTop: '1px solid #e5e7eb', padding: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
                Uploaded Documents
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {queue.items.map(guide => (
                  <div key={guide.guide_id} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', padding: '8px', background: '#f9fafb', borderRadius: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151', minWidth: '160px' }}>
                      {guide.guide_info?.name || guide.guide_id}
                    </span>
                    <DocumentLinks documents={guide.documents} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {queue.pagination.total > 0 && (
            <div className="border-t border-gray-200 p-4">
              <Pagination
                currentPage={Math.floor(filters.offset / filters.limit) + 1}
                totalPages={Math.ceil(queue.pagination.total / filters.limit)}
                onPageChange={(page) => handlePageChange((page - 1) * filters.limit)}
                totalItems={queue.pagination.total}
                itemsPerPage={filters.limit}
              />
            </div>
          )}
        </div>

        {/* Guide Details Modal */}
        {showDetailsModal && selectedGuideId && (
          <GuideDetailsModal
            guideId={selectedGuideId}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedGuideId(null);
              refresh(); // Refresh queue when closing modal
            }}
          />
        )}

        <ConfirmModal
          isOpen={approveConfirm.isOpen}
          onClose={() => setApproveConfirm({ isOpen: false, guideId: null })}
          onConfirm={() => executeApprove(approveConfirm.guideId)}
          title="Approve Guide"
          message="Are you sure you want to approve this guide?"
          confirmText="Approve"
          cancelText="Cancel"
          confirmButtonClass="bg-green-600 hover:bg-green-700"
        />

        <PromptModal
          isOpen={rejectPrompt.isOpen}
          onClose={() => setRejectPrompt({ isOpen: false, guideId: null })}
          onConfirm={executeReject}
          title="Reject Guide"
          message="Please provide a reason for rejection:"
          placeholder="Enter rejection reason..."
          confirmText="Reject"
          cancelText="Cancel"
        />

        <PromptModal
          isOpen={requestInfoPrompt.isOpen}
          onClose={() => setRequestInfoPrompt({ isOpen: false, guideId: null })}
          onConfirm={executeRequestInfo}
          title="Request Information"
          message="What information do you need from this guide?"
          placeholder="Enter notes about what is needed..."
          confirmText="Send Request"
          cancelText="Cancel"
        />

        <PromptModal
          isOpen={suspendPrompt.isOpen}
          onClose={() => setSuspendPrompt({ isOpen: false, guideId: null })}
          onConfirm={executeSuspend}
          title="Suspend Guide"
          message="Please provide a reason for suspending this guide:"
          placeholder="Enter suspension reason..."
          confirmText="Suspend"
          cancelText="Cancel"
        />

        {/* Toast notification */}
        {toast && (
          <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            padding: '12px 24px',
            borderRadius: '8px',
            backgroundColor: toast.type === 'error' ? '#dc2626' : '#16a34a',
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
          }}>
            {toast.message}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

/**
 * Modal component for viewing and managing guide approval details
 */
function GuideDetailsModal({ guideId, onClose }) {
  const { guide, loading, error: hookError, approveGuide, rejectGuide, updateStatus: _updateStatus, actionLoading } = useGuideApproval(guideId);
  const { requirements, updateRequirementStatus, createRequirement: _createRequirement, deleteRequirement } = useGuideRequirements(guideId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [rejectReasonPrompt, setRejectReasonPrompt] = useState({ isOpen: false });
  const [rejectNotesPrompt, setRejectNotesPrompt] = useState({ isOpen: false, reason: null });

  // Use hook error or local error
  const displayError = hookError || error;

  const handleApprove = () => {
    setShowApproveConfirm(true);
  };

  const executeApprove = async () => {
    setShowApproveConfirm(false);

    const result = await approveGuide();
    if (result.success) {
      setError('Guide approved successfully!');
      setTimeout(() => setError(null), 3000);
      onClose();
    } else {
      setError(`Failed to approve guide: ${result.error}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleReject = () => {
    setRejectReasonPrompt({ isOpen: true });
  };

  const handleRejectReasonSubmit = (reason) => {
    setRejectReasonPrompt({ isOpen: false });
    setRejectNotesPrompt({ isOpen: true, reason });
  };

  const handleRejectNotesSubmit = async (notes) => {
    const { reason } = rejectNotesPrompt;
    setRejectNotesPrompt({ isOpen: false, reason: null });

    const result = await rejectGuide(reason, notes);
    if (result.success) {
      setError('Guide rejected');
      setTimeout(() => setError(null), 3000);
      onClose();
    } else {
      setError(`Failed to reject guide: ${result.error}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  if (displayError) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
          <h3 className="text-lg font-medium text-red-900 mb-2">Error</h3>
          <p className="text-sm text-red-700 mb-4">{displayError}</p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!guide) {return null;}

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full m-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{guide.guide_info.name}</h2>
              <p className="text-sm text-gray-600">{guide.guide_info.email}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-2 px-1 text-sm font-medium ${
                activeTab === 'overview'
                  ? 'border-b-2 border-brand-primary text-brand-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('requirements')}
              className={`pb-2 px-1 text-sm font-medium ${
                activeTab === 'requirements'
                  ? 'border-b-2 border-brand-primary text-brand-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Requirements ({requirements.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`pb-2 px-1 text-sm font-medium ${
                activeTab === 'history'
                  ? 'border-b-2 border-brand-primary text-brand-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              History
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Business Name</label>
                  <p className="mt-1 text-sm text-gray-900">{guide.business_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Years Experience</label>
                  <p className="mt-1 text-sm text-gray-900">{guide.years_experience || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">License Number</label>
                  <p className="mt-1 text-sm text-gray-900">{guide.license?.number || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">License State</label>
                  <p className="mt-1 text-sm text-gray-900">{guide.license?.state || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Insurance Provider</label>
                  <p className="mt-1 text-sm text-gray-900">{guide.insurance?.provider || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Policy Number</label>
                  <p className="mt-1 text-sm text-gray-900">{guide.insurance?.policy_number || 'N/A'}</p>
                </div>
              </div>

              {guide.approval.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Admin Notes</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded text-sm text-gray-900">
                    {guide.approval.notes}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'requirements' && (
            <RequirementsList
              requirements={requirements}
              onUpdateStatus={updateRequirementStatus}
              onDelete={deleteRequirement}
              onAdd={() => { setError('Add requirement functionality would go here'); setTimeout(() => setError(null), 3000); }}
            />
          )}

          {activeTab === 'history' && (
            <div className="space-y-3">
              {guide.history && guide.history.length > 0 ? (
                guide.history.map((entry) => (
                  <div key={entry.id} className="border-l-4 border-brand-primary pl-4 py-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {entry.previous_status} → {entry.new_status}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(entry.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{entry.change_reason}</p>
                    {entry.notes && (
                      <p className="text-xs text-gray-500 mt-1">{entry.notes}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      By: {entry.changed_by.name}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No history available</p>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          {!guide.stripe_onboarding_complete && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0 text-amber-500" />
              Guide has not completed payment settings (Stripe). Approval is blocked until payouts are enabled.
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
            <button
              onClick={handleReject}
              disabled={actionLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              <XCircle className="w-4 h-4 inline mr-1" />
              Reject
            </button>
            <button
              onClick={handleApprove}
              disabled={actionLoading || !guide.stripe_onboarding_complete}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!guide.stripe_onboarding_complete ? 'Payment settings must be completed first' : undefined}
            >
              <CheckCircle className="w-4 h-4 inline mr-1" />
              Approve
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={executeApprove}
        title="Approve Guide"
        message="Are you sure you want to approve this guide?"
        confirmText="Approve"
        cancelText="Cancel"
        confirmButtonClass="bg-green-600 hover:bg-green-700"
      />

      <PromptModal
        isOpen={rejectReasonPrompt.isOpen}
        onClose={() => setRejectReasonPrompt({ isOpen: false })}
        onConfirm={handleRejectReasonSubmit}
        title="Reject Guide"
        message="Please provide a reason for rejection:"
        placeholder="Enter rejection reason..."
        confirmText="Next"
        cancelText="Cancel"
      />

      <PromptModal
        isOpen={rejectNotesPrompt.isOpen}
        onClose={() => setRejectNotesPrompt({ isOpen: false, reason: null })}
        onConfirm={handleRejectNotesSubmit}
        title="Additional Notes"
        message="Additional notes (optional):"
        placeholder="Enter additional notes..."
        confirmText="Reject"
        cancelText="Cancel"
        required={false}
      />
    </div>
  );
}
